/**
 * JS is basically a port  of the jetpack engine for the tiled-gallery content
 * That is the orignal code https://c0.wp.com/p/jetpack/16.0/_inc/build/tiled-gallery/tiled-gallery/tiled-gallery.min.js
*/
class BlogGallery extends HTMLElement {
    connectedCallback() {
        this.gallery = null;
        this.galleryImages = [];
        this.resizeObserver = null;

        this.originalGalleryWidth = 960;
        this.gallerySpacing = 4;

        this.galleryType = this.dataset.typeFiltered || "";
        this.galleryColumns = Number.parseInt(
            this.dataset.columnsFiltered || "",
            10
        );
        this.galleryLink = this.dataset.linkFiltered || "none";
        this.gallerySize = this.dataset.sizeFiltered || "";

        this.boundResize = this.resize.bind(this);

        if (
            Number.isFinite(this.galleryColumns) === false
            || this.galleryColumns < 1
        ) {
            this.galleryColumns = 1;
        }

        this.parseImages();
        this.buildGallery();
        this.setupResizeObserver();
        this.resize();
    }

    disconnectedCallback() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        window.removeEventListener("resize", this.boundResize);
    }

    parseImages() {
        const imagesValue = this.dataset.images || "";
        let parsedImages = [];

        this.galleryImages = [];

        if (imagesValue !== "") {
            try {
                const parsedValue = JSON.parse(imagesValue);

                if (Array.isArray(parsedValue)) {
                    parsedImages = parsedValue;
                }
            } catch (error) {
                parsedImages = imagesValue.split(",");
            }
        }

        for (let index = 0; index < parsedImages.length; index++) {
            const imageValue = parsedImages[index];
            let imageSource = "";
            let imageAlt = "";

            if (typeof imageValue === "string") {
                imageSource = imageValue.trim();
            } else if (
                imageValue
                && typeof imageValue === "object"
            ) {
                if (typeof imageValue.src === "string") {
                    imageSource = imageValue.src.trim();
                } else if (typeof imageValue.url === "string") {
                    imageSource = imageValue.url.trim();
                }

                if (typeof imageValue.alt === "string") {
                    imageAlt = imageValue.alt;
                }
            }

            if (imageSource !== "") {
                this.galleryImages.push({
                    src: imageSource,
                    alt: imageAlt
                });
            }
        }
    }

    buildGallery() {
        const gallery = document.createElement("div");

        gallery.className = [
            "tiled-gallery",
            "tiled-gallery-unresized",
            `type-${this.galleryType}`
        ].join(" ");

        gallery.dataset.originalWidth = String(this.originalGalleryWidth);
        gallery.dataset.galleryType = this.galleryType;
        gallery.dataset.galleryColumns = String(this.galleryColumns);
        gallery.dataset.galleryLink = this.galleryLink;
        gallery.dataset.gallerySize = this.gallerySize;

        if (
            this.galleryType === "rectangular"
            && this.galleryImages.length === 3
        ) {
            this.buildThreeImageRectangularGallery(gallery);
        } else if (
            this.galleryType === "rectangular"
            && this.galleryImages.length === 4
        ) {
            this.buildFourImageRectangularGallery(gallery);
        } else {
            this.buildStandardGallery(gallery);
        }

        this.replaceChildren(gallery);

        this.gallery = gallery;
        this.gallery.classList.remove("tiled-gallery-unresized");
    }

    buildThreeImageRectangularGallery(gallery) {
        const row = this.createRow(960, 640);

        const firstGroup = this.createGroup(
            1,
            640,
            640
        );

        const secondGroup = this.createGroup(
            2,
            320,
            640
        );

        const firstItem = this.createItem(
            this.galleryImages[0],
            636,
            636
        );

        const secondItem = this.createItem(
            this.galleryImages[1],
            316,
            316
        );

        const thirdItem = this.createItem(
            this.galleryImages[2],
            316,
            316
        );

        firstGroup.appendChild(firstItem);

        secondGroup.appendChild(secondItem);
        secondGroup.appendChild(thirdItem);

        row.appendChild(firstGroup);
        row.appendChild(secondGroup);

        gallery.appendChild(row);
    }

    buildFourImageRectangularGallery(gallery) {
        this.buildThreeImageRectangularGallery(gallery);

        const row = this.createRow(960, 640);
        const group = this.createGroup(
            1,
            960,
            640
        );

        const item = this.createItem(
            this.galleryImages[3],
            956,
            636
        );

        group.appendChild(item);
        row.appendChild(group);
        gallery.appendChild(row);
    }

    buildStandardGallery(gallery) {
        const imageCount = this.galleryImages.length;
        const columns = Math.min(
            Math.max(this.galleryColumns, 1),
            Math.max(imageCount, 1)
        );

        let imageIndex = 0;

        while (imageIndex < imageCount) {
            const remainingImages = imageCount - imageIndex;
            const imagesInRow = Math.min(columns, remainingImages);

            const itemOriginalWidth = Math.floor(
                this.originalGalleryWidth / imagesInRow
            );

            const rowOriginalWidth = itemOriginalWidth * imagesInRow;
            const rowOriginalHeight = itemOriginalWidth;

            const row = this.createRow(
                rowOriginalWidth,
                rowOriginalHeight
            );

            for (
                let columnIndex = 0;
                columnIndex < imagesInRow;
                columnIndex++
            ) {
                const imageData = this.galleryImages[imageIndex];
                const groupIndex = columnIndex + 1;

                const group = this.createGroup(
                    groupIndex,
                    itemOriginalWidth,
                    rowOriginalHeight
                );

                const imageOriginalWidth = Math.max(
                    itemOriginalWidth - this.gallerySpacing,
                    0
                );

                const imageOriginalHeight = Math.max(
                    rowOriginalHeight - this.gallerySpacing,
                    0
                );

                const item = this.createItem(
                    imageData,
                    imageOriginalWidth,
                    imageOriginalHeight
                );

                group.appendChild(item);
                row.appendChild(group);

                imageIndex += 1;
            }

            gallery.appendChild(row);
        }
    }

    createRow(originalWidth, originalHeight) {
        const row = document.createElement("div");

        row.className = "gallery-row";

        row.dataset.originalWidth = String(originalWidth);
        row.dataset.originalHeight = String(originalHeight);

        return row;
    }

    createGroup(index, originalWidth, originalHeight) {
        const group = document.createElement("div");

        group.className = `gallery-group images-${index}`;

        group.dataset.originalWidth = String(originalWidth);
        group.dataset.originalHeight = String(originalHeight);

        return group;
    }

    createItem(imageData, originalWidth, originalHeight) {
        const item = document.createElement("div");
        const image = document.createElement("img");

        item.className = [
            "tiled-gallery-item",
            "tiled-gallery-item-large"
        ].join(" ");

        item.setAttribute("itemprop", "associatedMedia");
        item.setAttribute("itemscope", "");
        item.setAttribute(
            "itemtype",
            "http://schema.org/ImageObject"
        );

        image.src = imageData.src;
        image.alt = imageData.alt || "";
        image.loading = "lazy";
        image.decoding = "async";

        image.dataset.originalWidth = String(originalWidth);
        image.dataset.originalHeight = String(originalHeight);

        image.width = originalWidth;
        image.height = originalHeight;

        image.setAttribute("itemprop", "image");

        if (
            this.galleryLink === "file"
            || this.galleryLink === "attachment"
        ) {
            const link = document.createElement("a");

            link.href = imageData.src;
            link.setAttribute("itemprop", "url");

            if (this.galleryLink === "file") {
                link.target = "_blank";
                link.rel = "noopener noreferrer";
            }

            link.appendChild(image);
            item.appendChild(link);
        } else {
            item.appendChild(image);
        }

        return item;
    }

    setupResizeObserver() {
        const resizeTarget = this.parentElement;

        if (
            typeof ResizeObserver !== "undefined"
            && resizeTarget
        ) {
            this.resizeObserver = new ResizeObserver(
                this.boundResize
            );

            this.resizeObserver.observe(resizeTarget);
        } else {
            window.addEventListener(
                "resize",
                this.boundResize
            );
        }
    }

    getExtraDimension(element, property, direction) {
        const elementStyles = getComputedStyle(element);
        let firstProperty = "";
        let secondProperty = "";
        let total = 0;

        if (direction === "horizontal") {
            if (property === "border") {
                firstProperty = "borderLeftWidth";
                secondProperty = "borderRightWidth";
            } else {
                firstProperty = `${property}Left`;
                secondProperty = `${property}Right`;
            }
        } else if (direction === "vertical") {
            if (property === "border") {
                firstProperty = "borderTopWidth";
                secondProperty = "borderBottomWidth";
            } else {
                firstProperty = `${property}Top`;
                secondProperty = `${property}Bottom`;
            }
        }

        if (
            firstProperty !== ""
            && secondProperty !== ""
        ) {
            total = (
                Number.parseFloat(elementStyles[firstProperty])
                || 0
            ) + (
                Number.parseFloat(elementStyles[secondProperty])
                || 0
            );
        }

        return total;
    }

    resize() {
        if (!this.gallery) {
            return;
        }

        const originalWidth = Number.parseInt(
            this.gallery.dataset.originalWidth,
            10
        );

        const parentElement = this.gallery.parentElement;

        let availableWidth = 0;

        if (parentElement) {
            availableWidth = Number.parseFloat(
                getComputedStyle(parentElement).width
            );
        }

        if (
            Number.isFinite(originalWidth)
            && originalWidth > 0
            && Number.isFinite(availableWidth)
            && availableWidth > 0
        ) {
            const scale = Math.min(
                1,
                availableWidth / originalWidth
            );

            const resizeableElements = this.gallery.querySelectorAll(
                [
                    ".gallery-row",
                    ".gallery-group",
                    ".tiled-gallery-item img"
                ].join(", ")
            );

            for (
                let index = 0;
                index < resizeableElements.length;
                index++
            ) {
                const element = resizeableElements[index];

                const marginHorizontal = this.getExtraDimension(
                    element,
                    "margin",
                    "horizontal"
                );

                const marginVertical = this.getExtraDimension(
                    element,
                    "margin",
                    "vertical"
                );

                const paddingHorizontal = this.getExtraDimension(
                    element,
                    "padding",
                    "horizontal"
                );

                const paddingVertical = this.getExtraDimension(
                    element,
                    "padding",
                    "vertical"
                );

                const borderHorizontal = this.getExtraDimension(
                    element,
                    "border",
                    "horizontal"
                );

                const borderVertical = this.getExtraDimension(
                    element,
                    "border",
                    "vertical"
                );

                const elementOriginalWidth = Number.parseInt(
                    element.dataset.originalWidth,
                    10
                );

                const elementOriginalHeight = Number.parseInt(
                    element.dataset.originalHeight,
                    10
                );

                if (
                    Number.isFinite(elementOriginalWidth)
                    && elementOriginalWidth >= 0
                    && Number.isFinite(elementOriginalHeight)
                    && elementOriginalHeight >= 0
                ) {
                    const calculatedWidth = (
                        elementOriginalWidth
                        + paddingHorizontal
                        + borderHorizontal
                        + marginHorizontal
                    );

                    const calculatedHeight = (
                        elementOriginalHeight
                        + paddingVertical
                        + borderVertical
                        + marginVertical
                    );

                    element.style.width = `${
                        Math.floor(scale * calculatedWidth)
                        - marginHorizontal
                    }px`;

                    element.style.height = `${
                        Math.floor(scale * calculatedHeight)
                        - marginVertical
                    }px`;
                }
            }
        }
    }
}

if (!customElements.get("blog-gallery")) {
    customElements.define("blog-gallery", BlogGallery);
}